import { mediaQuery } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

export const getWrapperStyles = ({ customStyles }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',

  ...customStyles,
});

export const headerStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '20px',
};

export const addButtonStyles = {
  '&:hover': {
    opacity: '0.6',
    backgroundColor: 'transparent',
  },
};

export const dividerStyles = {
  borderColor: ({ palette }) => palette.border.default,
};

export const tableContainerStyles = {
  borderRadius: '5px',
  border: ({ palette }) => `1px solid ${palette.border.default}`,
  overflowX: 'auto',

  [mediaQuery.mobile]: {
    overflowX: 'hidden',

    '& thead': {
      display: 'none',
    },

    '& tbody tr': {
      display: 'block',
      borderBottom: ({ palette }) => `1px solid ${palette.border.default}`,
      borderRadius: '8px',
      margin: '8px',
      padding: '8px 12px',

      '&:last-of-type': {
        marginBottom: '8px',
      },
    },

    '& tbody td': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '8px',
      border: 'none !important',
      width: 'auto !important',
      whiteSpace: 'normal !important',
      overflow: 'visible',
      padding: '6px 0 !important',

      '&::before': {
        content: 'attr(data-label)',
        flexShrink: 0,
        fontWeight: 600,
        color: ({ palette }) => palette.text.secondary,
      },
    },
  },
};

export const tableStyles = {
  tableLayout: 'fixed',
  width: '100%',
  [mediaQuery.mobileAndTablet]: {
    tableLayout: 'auto',
    minWidth: '600px',
  },
  [mediaQuery.mobile]: {
    minWidth: '0',
    width: '100%',
  },
};

export const rowStyles = ({ isHover, isAdditionalRowContentVisible }) => ({
  cursor: isHover ? 'pointer' : 'default',

  ...getConditionalObject(isAdditionalRowContentVisible, {
    '& .table-cell': {
      borderBottom: 'none',
    },
  }),

  '&:last-of-type': {
    '& .table-cell': {
      borderBottom: 'none',
    },
  },
});

const commonCellStyles = {
  padding: '15px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderBottom: ({ palette }) => `1px solid ${palette.border.default}`,
};

export const getHeadCellStyles = ({ width, minWidth, withInternalBorders, customStyles }) => ({
  ...commonCellStyles,
  width,
  fontWeight: 600,
  fontSize: '1.4rem',
  lineHeight: '1.6rem',

  ...(withInternalBorders && {
    '&:not(:last-of-type)': {
      borderRight: ({ palette }) => `1px solid ${palette.border.default}`,
    },
  }),

  ...customStyles,

  ...getConditionalObject(!!minWidth, {
    minWidth,
  }),
});

export const getBodyCellStyles = ({
  withInternalBorders,
  customStyles,
  isModified,
  width,
  minWidth,
}) => ({
  ...commonCellStyles,
  padding: '14px 15px',
  fontSize: '1.4rem',
  fontWeight: 400,
  lineHeight: '2.2rem',
  color: ({ palette }) => palette.text.secondary,

  ...(withInternalBorders && {
    '&:not(:last-of-type)': {
      borderRight: ({ palette }) => `1px solid ${palette.border.default}`,
    },
  }),

  ...customStyles,

  ...getConditionalObject(isModified, {
    padding: '8px',
  }),

  ...getConditionalObject(!!width, {
    width,
  }),

  ...getConditionalObject(!!minWidth, {
    minWidth,
  }),
});

export const typographyStyles = {
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const flexCellStyles = ({ align, isModified, isAdditionalRowContentVisible }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: align,
  alignItems: 'center',

  ...(isModified
    ? {
        float: align,
      }
    : { width: '100%' }),

  ...getConditionalObject(isModified && align === 'center', {
    margin: '0 auto',
    float: 'unset',
  }),
  ...getConditionalObject(isAdditionalRowContentVisible, {
    borderBottom: '0 !important',

    '& .table-cell': {
      borderBottom: '0 !important',
    },
  }),
});

export const titleWrapperStyles = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  width: '100%',
};

export const titleContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

export const pendingChangesContainerStyles = {
  padding: '1px 8px',

  '& .pending-changes-text': {
    fontWeight: 400,
  },
};

export const searchControlsWrapperStyles = {
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  alignItems: 'center',
  justifyContent: 'space-between',
};
